import React from 'react';
import { Button } from './ui/button';
import { Check, ArrowRight, Sparkles, Star } from 'lucide-react';

const pricingPlans = [
  {
    id: 1,
    tier: "Tier 1",
    name: "Proof Sprint",
    price: "$9,950",
    priceNote: "+ equity",
    tagline: "Build proof investors can believe.",
    bestFor: "Founders who want to validate fundability fast.",
    outcomes: [
      "You have a clear, fundable starting point",
      "You have a working AI POC",
      "You can confidently start investor conversations"
    ],
    outcomesTitle: "What becomes true after this tier",
    features: [
      "Idea vetting & sculpting (investor-calibrated)",
      "Fundable starting point (seed-ready framing)",
      "Fully functional AI proof of concept",
      "Founder-owned codebase",
      "Slack communication"
    ],
    featuresTitle: "What's included",
    highlighted: false
  },
  {
    id: 2,
    tier: "Tier 2",
    name: "Raise-Ready Sprint",
    price: "$19,950",
    priceNote: "+ equity",
    tagline: "Turn proof into a credible raise.",
    bestFor: "Founders actively raising or preparing to raise.",
    outcomes: [
      "You don't just have a demo — you have a story",
      "Your company looks real before traction",
      "Investors understand why this wins"
    ],
    outcomesTitle: "What becomes true after this tier",
    features: [
      "Investor-grade pitch deck (10–12 slides)",
      "Demo video (90–120 seconds)",
      "Simple raise-ready website",
      "Clear narrative aligned with the POC"
    ],
    featuresTitle: "Everything in Tier 1, plus",
    highlighted: true
  }
];

const Pricing = () => {
  return (
    <section id="pricing" className="bg-black py-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-white/40 text-sm uppercase tracking-widest mb-4">
            Our Pricing
          </p>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Fixed-scope sprints.<br />Fundable outcomes.
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            No contract term. Pause or cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-8 transition-all duration-300 ${
                plan.highlighted
                  ? 'bg-white text-black shadow-2xl shadow-white/10'
                  : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
              }`}
            >
              {/* Badge */}
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 bg-black text-white text-xs font-medium px-3 py-1 rounded-full">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}

              {/* Tier Label */}
              <div className="mb-4">
                <span className={`text-xs font-medium uppercase tracking-widest ${
                  plan.highlighted ? 'text-black/40' : 'text-white/40'
                }`}>
                  {plan.tier}
                </span>
              </div>

              {/* Plan Name */}
              <h3 className={`text-2xl font-bold mb-2 ${
                plan.highlighted ? 'text-black' : 'text-white'
              }`}>
                {plan.name}
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-3">
                <span className={`text-4xl font-bold ${
                  plan.highlighted ? 'text-black' : 'text-white'
                }`}>
                  {plan.price}
                </span>
                <span className={`text-sm font-medium ${
                  plan.highlighted ? 'text-black/60' : 'text-white/60'
                }`}>
                  {plan.priceNote}
                </span>
              </div>

              {/* Tagline */}
              <p className={`text-lg mb-4 ${
                plan.highlighted ? 'text-black/80' : 'text-white/80'
              }`}>
                {plan.tagline}
              </p>

              {/* Best For */}
              <div className={`inline-block px-3 py-1.5 rounded-full text-xs font-medium mb-6 ${
                plan.highlighted 
                  ? 'bg-black/10 text-black/70' 
                  : 'bg-white/10 text-white/70'
              }`}>
                Best for: {plan.bestFor}
              </div>

              {/* CTA Button */}
              <Button
                className={`w-full rounded-full py-6 font-medium mb-8 ${
                  plan.highlighted
                    ? 'bg-black text-white hover:bg-black/90'
                    : 'bg-white text-black hover:bg-white/90'
                }`}
                onClick={() => window.open('https://calendly.com/superboonie', '_blank')}
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              {/* Outcomes Section */}
              <div className="mb-6">
                <p className={`text-sm font-semibold mb-4 flex items-center gap-2 ${
                  plan.highlighted ? 'text-black' : 'text-white'
                }`}>
                  <Star className="w-4 h-4" />
                  {plan.outcomesTitle}
                </p>
                <ul className="space-y-3">
                  {plan.outcomes.map((outcome, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        plan.highlighted ? 'bg-black/10' : 'bg-white/10'
                      }`}>
                        <Check className={`w-3 h-3 ${
                          plan.highlighted ? 'text-black' : 'text-white'
                        }`} />
                      </div>
                      <span className={`text-sm ${
                        plan.highlighted ? 'text-black/70' : 'text-white/70'
                      }`}>
                        {outcome}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Divider */}
              <div className={`border-t my-6 ${
                plan.highlighted ? 'border-black/10' : 'border-white/10'
              }`} />

              {/* Features Section */}
              <div>
                <p className={`text-sm font-semibold mb-4 ${
                  plan.highlighted ? 'text-black' : 'text-white'
                }`}>
                  {plan.featuresTitle}
                </p>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        plan.highlighted ? 'text-black/60' : 'text-white/60'
                      }`} />
                      <span className={`text-sm ${
                        plan.highlighted ? 'text-black/60' : 'text-white/60'
                      }`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Book a Call CTA */}
        <div className="mt-12 text-center">
          <p className="text-white/50 mb-4">Not sure which tier is right for you?</p>
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 font-medium"
            onClick={() => window.open('https://calendly.com/superboonie', '_blank')}
          >
            Book a 15-min call
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
